// src/app/services/ticker.service.ts
import { Injectable, inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { ChallengeService } from './challenge.service';

@Injectable({ providedIn: 'root' })
export class TickerService {
  private auth = inject(Auth);
  private challenges = inject(ChallengeService);

  private heartbeat: any = null;
  private deadlineTimer: any = null;
  private nextCutoverMs: number | null = null;
  private running = false;

  // ⏱️ Dev: activa cortes cada 30s en local (sin tocar lógica real)
  private get devFastCutover(): boolean {
    return typeof window !== 'undefined' &&
           window.location.hostname === 'localhost' &&
           localStorage.getItem('challup_dev_cutover') === '1';
  }

  start() {
    if (this.running) return;
    this.running = true;

    // Heartbeat de respaldo (1s): recupera si HMR o focus cambian cosas
    this.heartbeat = setInterval(() => this.tick(), 1000);

    // Programa el primer corte exacto
    this.scheduleNextCutover().catch(() => {});
  }

  stop() {
    this.running = false;
    if (this.heartbeat) clearInterval(this.heartbeat);
    if (this.deadlineTimer) clearTimeout(this.deadlineTimer);
    this.heartbeat = null;
    this.deadlineTimer = null;
    this.nextCutoverMs = null;
  }

  // Recalcula y programa el timer exacto al siguiente corte
  private async scheduleNextCutover() {
    const uid = this.auth.currentUser?.uid;
    if (!uid) return;

    try {
      if (this.devFastCutover) {
        // 👇 MODO DEV: siguiente corte al próximo múltiplo de 30s
        const now = Date.now();
        const slot = 30_000;
        this.nextCutoverMs = now - (now % slot) + slot;
      } else {
        const info = await this.challenges.getWindowInfo(uid);
        this.nextCutoverMs = info.nextCutoverMs;
      }

      // Limpia timer anterior y programa el nuevo
      if (this.deadlineTimer) clearTimeout(this.deadlineTimer);
      const delay = Math.max(0, (this.nextCutoverMs ?? Date.now()) - Date.now());
      this.deadlineTimer = setTimeout(() => this.fireCutover(), delay);
    } catch {
      // Reintento suave en 3s si algo falló (p. ej. red)
      setTimeout(() => this.scheduleNextCutover(), 3000);
    }
  }

  // Heartbeat de 1s para cubrir edge cases (pérdida de focus, HMR, etc.)
  private async tick() {
    if (!this.running) return;
    const uid = this.auth.currentUser?.uid;
    if (!uid) return;

    if (!this.nextCutoverMs || Date.now() >= this.nextCutoverMs) {
      // Si por lo que sea el timeout no saltó, fuerza el corte
      await this.fireCutover();
    }
  }

  // Ejecuta el corte: sincroniza día y emite evento global
  private async fireCutover() {
    const uid = this.auth.currentUser?.uid;
    if (!uid) {
      // Si no hay usuario, reprográmate cuando lo haya
      this.nextCutoverMs = null;
      return;
    }
    try {
      await this.challenges.syncDayFromAnchor(uid);
      this.nextCutoverMs = null;
      // 🔔 Notifica a páginas (Explore/Feed/Today)
      window.dispatchEvent(new CustomEvent('challup:cutover'));
    } catch {
      // Ignora fallos momentáneos
    } finally {
      // Reprograma el siguiente corte
      this.scheduleNextCutover().catch(() => {});
    }
  }
}
